import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Label,
  Input,
  Button,
  Alert,
  Table,
  Modal,
  ModalBody,
} from 'reactstrap';
import { toast } from 'react-toastify';

import {
  downloadPanitiaNotaDinas,
  downloadSupportingDocument,
} from '../tenderOpeningApi';
import { formatDate } from '../tenderOpeningFormatters';
import { HeaderTenderCard, CargoRequirementCard } from './TenderInfoCards';

// Tender Opening Detail > Sending Invitation to Panitia Tender — layout
// mengikuti desain acuan:
//   1. Form "Input Nota Dinas & Jadwal Rapat Panitia Tender" — judul/nomor/
//      tanggal Nota Dinas, Meeting Start/End (WIB), lokasi, agenda, upload file
//      Nota Dinas. Save as Draft (upsert, endpoint 9a) + Send (endpoint 9,
//      multipart) yang mengunci tab setelah terkirim.
//   2. Informasi Header Tender + Cargo Requirement (kartu bersama).
//   3. Dokumen Lampiran Undangan (Internal & DMUT) — SEMUA supporting document
//      dengan badge akses tagging-nya.
//   4. Mitra Usaha Terundang (Invited DMUT).
//   5. Footer: Kembali ke List, Hard Recall, Sending Invitation to Panitia
//      Tender.

// "2026-07-19T17:00:00Z" -> "2026-07-20" for the <input type="date"> value.
// Wall-clock Jakarta, BUKAN toISOString(): server menyimpan tanggal dengan
// offset +07:00, jadi slice UTC akan mundur satu hari.
const toDateInputValue = value => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Jakarta' });
};

const SendPanitiaTab = ({
  record,
  detail,
  sent,
  editable,
  onSaveDraft,
  savingDraft,
  onSend,
  sending,
  canHardRecall,
  onHardRecall,
  recipients,
  loadingRecipients,
  onBack,
}) => {
  const p = detail?.panitia || {};
  const cargos = detail?.requirement?.cargos || [];
  const docs = detail?.supportingDocuments || [];
  const rows = recipients || [];
  const hasAnyPic = rows.some(r => (r.pics || []).length > 0);

  const [form, setForm] = useState({
    notaDinasTitle: p.notaDinasTitle || '',
    notaDinasNo: p.notaDinasNo || '',
    notaDinasDate: toDateInputValue(p.notaDinasDate),
    meetingDate: p.tanggal || '',
    startTime: p.startTime || '',
    endTime: p.endTime || '',
    isUntilFinished: !!p.hinggaSelesai,
    location: p.lokasi || '',
    agenda: p.agenda || '',
    file: null,
  });

  // Re-sync with server truth whenever Detail reloads (after a successful
  // Save Draft / Send) — not on every unrelated re-render.
  useEffect(() => {
    setForm({
      notaDinasTitle: p.notaDinasTitle || '',
      notaDinasNo: p.notaDinasNo || '',
      notaDinasDate: toDateInputValue(p.notaDinasDate),
      meetingDate: toDateInputValue(p.tanggal),
      startTime: p.startTime || '',
      meetingEndDate: toDateInputValue(p.tanggalEnd),
      endTime: p.endTime || '',
      isUntilFinished: !!p.hinggaSelesai,
      location: p.lokasi || '',
      agenda: p.agenda || '',
      file: null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    p.notaDinasTitle,
    p.notaDinasNo,
    p.notaDinasDate,
    p.tanggal,
    p.startTime,
    p.tanggalEnd,
    p.endTime,
    p.hinggaSelesai,
    p.lokasi,
    p.agenda,
  ]);

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const readOnly = !editable;

  // Modal "Konfirmasi Kirim Undangan Panitia Tender" (desain acuan) — tampil
  // setelah form lolos validasi, sebelum Send benar-benar dieksekusi.
  const [confirmOpen, setConfirmOpen] = useState(false);

  const buildPayload = () => ({
    meetingDate: form.meetingDate,
    startTime: form.startTime,
    // Tanggal selesai default sama dengan tanggal mulai kalau tidak diisi.
    meetingEndDate: form.meetingEndDate || form.meetingDate,
    endTime: form.endTime,
    isUntilFinished: form.isUntilFinished,
    location: form.location,
    agenda: form.agenda,
    notaDinasTitle: form.notaDinasTitle,
    notaDinasNumber: form.notaDinasNo,
    notaDinasDate: form.notaDinasDate,
    file: form.file,
  });

  const handleSaveDraft = () => onSaveDraft(buildPayload());

  const handleSend = () => {
    if (
      !form.notaDinasTitle ||
      !form.notaDinasNo ||
      !form.notaDinasDate ||
      !form.meetingDate ||
      !form.startTime ||
      !form.location ||
      !form.agenda ||
      (!form.isUntilFinished && !form.endTime)
    ) {
      toast.error(
        'Judul/Nomor/Tanggal Nota Dinas, Meeting Start, Lokasi, dan Agenda wajib diisi (Meeting End wajib kecuali "Hingga selesai").'
      );
      return;
    }
    if (!form.file && !p.notaDinasDocument) {
      toast.error('File Nota Dinas Undangan Evaluasi Tender wajib diunggah.');
      return;
    }
    setConfirmOpen(true);
  };

  const confirmSend = () => {
    setConfirmOpen(false);
    onSend(buildPayload());
  };

  const handlePreviewNotaDinas = async () => {
    try {
      const blob = await downloadPanitiaNotaDinas(record.id);
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(`Preview unavailable: ${error}`);
    }
  };

  const handleDocAction = async (doc, mode) => {
    try {
      const ownerId =
        doc.origin === 'planning'
          ? record.planningId
          : record.procurementValidityId;
      const blob = await downloadSupportingDocument(
        doc.origin,
        ownerId,
        doc.id
      );
      const url = window.URL.createObjectURL(blob);
      if (mode === 'preview') {
        window.open(url, '_blank', 'noopener');
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.download = doc.fileName || doc.name;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(`Failed: ${error}`);
    }
  };

  return (
    <div>
      {!sent && (
        <Alert color='warning' className='d-flex align-items-center'>
          <i className='ri-alert-line fs-5 me-2'></i>
          Lakukan pengiriman Memo/Nota Dinas undangan kepada Panitia Tender
          melalui P-Office terlebih dahulu untuk dapat menginputkan kebutuhan
          informasi dibawah ini.
        </Alert>
      )}

      {/* ---- 1. Input Nota Dinas & Jadwal Rapat Panitia Tender ---- */}
      <Card className='border shadow-none mb-3'>
        <CardHeader className='bg-light'>
          <span className='fw-semibold'>
            <i className='ri-file-text-line align-bottom me-2'></i>
            Input Nota Dinas &amp; Jadwal Rapat Panitia Tender
          </span>
        </CardHeader>
        <CardBody>
          <Row className='g-3 align-items-center'>
            <Col md={3}>
              <Label className='form-label mb-0'>Judul Nota Dinas</Label>
            </Col>
            <Col md={9}>
              <Input
                value={form.notaDinasTitle}
                onChange={e => setField('notaDinasTitle', e.target.value)}
                readOnly={readOnly}
                disabled={readOnly}
                placeholder={
                  readOnly
                    ? '-'
                    : 'Undangan Rapat Pembukaan dan Evaluasi Penawaran - ...'
                }
              />
            </Col>

            <Col md={3}>
              <Label className='form-label mb-0'>Nomor Nota Dinas</Label>
            </Col>
            <Col md={9}>
              <Input
                value={form.notaDinasNo}
                onChange={e => setField('notaDinasNo', e.target.value)}
                readOnly={readOnly}
                disabled={readOnly}
                placeholder={readOnly ? '-' : 'e.g. ND-0024/KPI/PROC/2026'}
              />
            </Col>

            <Col md={3}>
              <Label className='form-label mb-0'>Tanggal Nota Dinas</Label>
            </Col>
            <Col md={9}>
              {readOnly ? (
                <Input
                  value={
                    form.notaDinasDate ? formatDate(form.notaDinasDate) : '-'
                  }
                  readOnly
                  disabled
                />
              ) : (
                <Input
                  type='date'
                  value={form.notaDinasDate}
                  onChange={e => setField('notaDinasDate', e.target.value)}
                />
              )}
            </Col>

            <Col md={3}>
              <Label className='form-label mb-0'>Meeting Start (WIB)</Label>
            </Col>
            <Col md={9}>
              <div className='d-flex gap-2'>
                {readOnly ? (
                  <Input
                    value={
                      form.meetingDate
                        ? `${formatDate(form.meetingDate)} - ${form.startTime || '-'} WIB`
                        : '-'
                    }
                    readOnly
                    disabled
                  />
                ) : (
                  <>
                    <Input
                      type='date'
                      value={form.meetingDate}
                      onChange={e => setField('meetingDate', e.target.value)}
                    />
                    <Input
                      type='time'
                      style={{ maxWidth: 140 }}
                      value={form.startTime}
                      onChange={e => setField('startTime', e.target.value)}
                    />
                  </>
                )}
              </div>
            </Col>

            <Col md={3}>
              <Label className='form-label mb-0'>Meeting End (WIB)</Label>
            </Col>
            <Col md={9}>
              <div className='d-flex align-items-center gap-3 flex-wrap'>
                {readOnly ? (
                  <Input
                    style={{ maxWidth: 320 }}
                    value={
                      form.isUntilFinished
                        ? 'Hingga selesai'
                        : form.meetingEndDate || form.endTime
                          ? `${form.meetingEndDate ? formatDate(form.meetingEndDate) : '-'} - ${form.endTime || '-'} WIB`
                          : '-'
                    }
                    readOnly
                    disabled
                  />
                ) : (
                  <>
                    <Input
                      type='date'
                      style={{ maxWidth: 200 }}
                      value={form.meetingEndDate}
                      min={form.meetingDate || undefined}
                      onChange={e => setField('meetingEndDate', e.target.value)}
                      disabled={form.isUntilFinished}
                    />
                    <Input
                      type='time'
                      style={{ maxWidth: 140 }}
                      value={form.endTime}
                      onChange={e => setField('endTime', e.target.value)}
                      disabled={form.isUntilFinished}
                    />
                  </>
                )}
                <div className='form-check form-switch mb-0'>
                  <Input
                    type='checkbox'
                    role='switch'
                    className='form-check-input'
                    checked={!!form.isUntilFinished}
                    disabled={readOnly}
                    onChange={e =>
                      setField('isUntilFinished', e.target.checked)
                    }
                  />
                  <Label className='form-check-label'>Hingga selesai</Label>
                </div>
              </div>
            </Col>

            <Col md={3}>
              <Label className='form-label mb-0'>Lokasi Kegiatan</Label>
            </Col>
            <Col md={9}>
              <Input
                value={form.location}
                onChange={e => setField('location', e.target.value)}
                readOnly={readOnly}
                disabled={readOnly}
                placeholder={
                  readOnly
                    ? '-'
                    : 'Ruang Rapat Utama Lt. 12 ... & MS Teams Virtual Meeting'
                }
              />
            </Col>

            <Col md={3}>
              <Label className='form-label mb-0'>Agenda Kegiatan</Label>
            </Col>
            <Col md={9}>
              <Input
                type='textarea'
                rows={3}
                value={form.agenda}
                onChange={e => setField('agenda', e.target.value)}
                readOnly={readOnly}
                disabled={readOnly}
                placeholder={
                  readOnly
                    ? '-'
                    : 'Pembukaan Dokumen Penawaran, Klarifikasi Aspek Teknis, ...'
                }
              />
            </Col>

            <Col md={3}>
              <Label className='form-label mb-0'>
                Upload Nota Dinas Undangan Evaluasi Tender
              </Label>
            </Col>
            <Col md={9}>
              <div className='border border-dashed rounded px-3 py-2 bg-light'>
                <div className='d-flex align-items-center gap-2'>
                  <i className='ri-attachment-2 text-muted'></i>
                  <span className='flex-grow-1 text-muted fst-italic fs-13 text-truncate'>
                    {form.file
                      ? `File terpilih: ${form.file.name}`
                      : p.notaDinasDocument
                        ? p.notaDinasDocument
                        : 'Belum ada file nota dinas yang diunggah'}
                  </span>
                  {p.notaDinasDocument && !form.file && (
                    <button
                      type='button'
                      className='btn btn-soft-secondary btn-sm btn-icon'
                      title='Preview'
                      onClick={handlePreviewNotaDinas}
                    >
                      <i className='ri-eye-line align-bottom'></i>
                    </button>
                  )}
                  {!readOnly && (
                    <label className='btn btn-light border btn-sm mb-0'>
                      <i className='ri-upload-2-line align-bottom me-1'></i>
                      Browse File...
                      <input
                        type='file'
                        hidden
                        accept='.pdf,.jpg,.jpeg'
                        onChange={e =>
                          setField('file', e.target.files?.[0] || null)
                        }
                      />
                    </label>
                  )}
                </div>
                <small className='text-danger d-block mt-1'>
                  Max 1 File ~ 100 MB, Format PDF / JPG
                </small>
              </div>
            </Col>
          </Row>

          <div className='d-flex gap-2 justify-content-end mt-3'>
            <Button
              color='secondary'
              outline
              disabled={readOnly || sent || savingDraft || sending}
              title={readOnly ? 'View only' : undefined}
              onClick={handleSaveDraft}
            >
              <i className='ri-draft-line align-bottom me-1'></i>
              {savingDraft ? 'Saving...' : 'Save as Draft'}
            </Button>
            <Button
              color='primary'
              disabled={readOnly || sent || sending || savingDraft}
              title={readOnly ? 'View only' : undefined}
              onClick={handleSend}
            >
              <i className='ri-send-plane-line align-bottom me-1'></i>
              {sent
                ? 'Invitation Panitia Terkirim'
                : sending
                  ? 'Mengirim...'
                  : 'Send Invitation to Panitia Tender'}
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* ---- 2. Informasi Header Tender + Cargo Requirement ---- */}
      <Alert color='info' className='d-flex align-items-center'>
        <i className='ri-information-line fs-5 me-2'></i>
        Informasi detail rencana Pengadaan Import Minyak Mentah/Kondensat
        dibawah ini dapat diunduh untuk dapat dijadikan lampiran dalam pengajuan
        Nota Dinas
      </Alert>

      <HeaderTenderCard record={record} detail={detail} />
      <CargoRequirementCard cargos={cargos} />

      {/* ---- 3. Dokumen Lampiran Undangan (Internal & DMUT) ---- */}
      <Card className='border shadow-none mb-3'>
        <CardHeader className='bg-light d-flex align-items-center'>
          <span className='fw-semibold flex-grow-1'>
            <i className='ri-attachment-2 align-bottom me-2'></i>
            Dokumen Lampiran Undangan (Internal &amp; DMUT)
          </span>
          <span className='badge bg-primary-subtle text-primary border'>
            Semua Dokumen Terupload
          </span>
        </CardHeader>
        <CardBody className='p-0'>
          <div className='table-responsive'>
            <Table className='table-sm align-middle table-nowrap mb-0'>
              <thead className='table-light'>
                <tr className='text-uppercase fs-12 text-muted'>
                  <th style={{ width: 60 }}>No</th>
                  <th>Nama Dokumen</th>
                  <th>Asal Sumber</th>
                  <th>Terakhir Diperbarui</th>
                  <th className='text-center'>Akses Tagging</th>
                  <th className='text-center'>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {docs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className='text-center text-muted py-3'>
                      Tidak ada dokumen terupload
                    </td>
                  </tr>
                ) : (
                  docs.map((d, idx) => (
                    <tr key={d.id}>
                      <td className='text-muted'>{idx + 1}</td>
                      <td>
                        <i className='ri-file-text-line text-primary me-2'></i>
                        <span className='fw-semibold'>{d.name}</span>
                      </td>
                      <td className='text-uppercase fs-12'>
                        {d.source === 'master'
                          ? 'Master Document'
                          : 'Upload Custom'}
                      </td>
                      <td className='font-monospace fs-12'>
                        {formatDate(d.lastUpdated)}
                      </td>
                      <td className='text-center'>
                        <span
                          className={`badge border ${
                            d.showToDmut
                              ? 'bg-success-subtle text-success'
                              : 'bg-secondary-subtle text-secondary'
                          }`}
                        >
                          {d.showToDmut
                            ? 'Visible to DMUT & Internal'
                            : 'Internal Only'}
                        </span>
                      </td>
                      <td className='text-center'>
                        <div className='d-flex gap-2 justify-content-center'>
                          <Button
                            color='light'
                            className='border'
                            size='sm'
                            onClick={() => handleDocAction(d, 'preview')}
                          >
                            <i className='ri-eye-line align-bottom me-1'></i>
                            Preview
                          </Button>
                          <Button
                            color='info'
                            outline
                            size='sm'
                            onClick={() => handleDocAction(d, 'download')}
                          >
                            <i className='ri-download-2-line align-bottom me-1'></i>
                            Download
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>

      {/* ---- 4. Mitra Usaha Terundang (Invited DMUT) ---- */}
      <Card className='border shadow-none mb-3'>
        <CardHeader className='bg-light d-flex align-items-center'>
          <span className='fw-semibold flex-grow-1'>
            <i className='ri-building-line align-bottom me-2'></i>
            Mitra Usaha Terundang (Invited DMUT)
          </span>
          <span className='badge bg-primary-subtle text-primary border'>
            Total DMUT: {rows.length} Vendor
          </span>
        </CardHeader>
        <CardBody className='p-0'>
          {loadingRecipients ? (
            <p className='text-muted fs-12 m-3'>Loading recipients...</p>
          ) : rows.length === 0 || !hasAnyPic ? (
            <p className='text-muted fst-italic m-3'>
              No DMUT recipients with a PIC were selected during Planning.
            </p>
          ) : (
            <div className='table-responsive'>
              <Table className='table-sm align-middle table-nowrap mb-0'>
                <thead className='table-light'>
                  <tr className='text-uppercase fs-12 text-muted'>
                    <th style={{ width: 60 }}>No</th>
                    <th>Nama Mitra Usaha (DMUT)</th>
                    <th>Nama PIC</th>
                    <th>Email PIC</th>
                    <th className='text-center'>Akses Undangan</th>
                  </tr>
                </thead>
                <tbody>
                  {rows
                    .flatMap(r =>
                      (r.pics || []).length === 0
                        ? [
                            {
                              key: r.id,
                              dmutName: r.dmutName,
                              picName: '',
                              picEmail: '',
                            },
                          ]
                        : r.pics.map(pic => ({
                            key: `${r.id}-${pic.id}`,
                            dmutName: r.dmutName,
                            picName: pic.picName || '',
                            picEmail: pic.picEmail || '',
                          }))
                    )
                    .map((v, idx) => (
                      <tr key={v.key}>
                        <td className='text-muted'>{idx + 1}</td>
                        <td>
                          <i className='ri-building-line text-muted me-2'></i>
                          <span className='fw-semibold'>{v.dmutName}</span>
                        </td>
                        <td>{v.picName || '-'}</td>
                        <td>
                          <span className='font-monospace text-primary fs-12'>
                            {v.picEmail || '-'}
                          </span>
                        </td>
                        <td className='text-center'>
                          <span
                            className={`badge border ${
                              sent
                                ? 'bg-success-subtle text-success'
                                : 'bg-light text-muted'
                            }`}
                          >
                            {sent ? 'Invitation Sent' : 'Ready for Dispatch'}
                          </span>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* ---- 5. Footer actions ---- */}
      <div className='d-flex flex-wrap gap-2 align-items-center'>
        <Button color='light' className='border' onClick={onBack}>
          <i className='ri-arrow-left-line align-bottom me-1'></i>
          Kembali ke List
        </Button>
        <div className='flex-grow-1'></div>
        <Button
          color='danger'
          outline
          disabled={!canHardRecall}
          onClick={onHardRecall}
          title={canHardRecall ? undefined : 'Not available'}
        >
          <i className='ri-arrow-go-back-line align-bottom me-1'></i>
          Hard Recall
        </Button>
        <Button
          color='primary'
          disabled={readOnly || sent || sending}
          title={readOnly ? 'View only' : undefined}
          onClick={handleSend}
        >
          <i className='ri-send-plane-line align-bottom me-1'></i>
          {sent
            ? 'Invitation Panitia Terkirim'
            : sending
              ? 'Mengirim...'
              : 'Sending Invitation to Panitia Tender'}
        </Button>
      </div>

      {/* ---- Modal Konfirmasi Kirim Undangan Panitia Tender (desain acuan) ---- */}
      <Modal
        isOpen={confirmOpen}
        toggle={() => setConfirmOpen(false)}
        centered
        size='lg'
      >
        <div
          className='d-flex align-items-center gap-3 px-4 py-3 rounded-top'
          style={{ backgroundColor: '#155e75' }}
        >
          <span
            className='d-flex align-items-center justify-content-center rounded text-white flex-shrink-0'
            style={{
              width: 42,
              height: 42,
              backgroundColor: 'rgba(255,255,255,.15)',
            }}
          >
            <i className='ri-team-line fs-4'></i>
          </span>
          <div className='flex-grow-1'>
            <h5 className='text-white mb-0 fw-semibold'>
              Konfirmasi Kirim Undangan Panitia Tender
            </h5>
            <small className='text-white-50'>
              Invitation to Panitia Tender Observer &amp; Evaluation
            </small>
          </div>
          <button
            type='button'
            className='btn-close btn-close-white'
            aria-label='Close'
            onClick={() => setConfirmOpen(false)}
          ></button>
        </div>
        <ModalBody className='p-4'>
          <p className='mb-3'>
            Apakah Anda yakin akan mengirimkan{' '}
            <b>{form.notaDinasTitle || 'Undangan Rapat Panitia Tender'}</b>{' '}
            untuk paket pengadaan berikut kepada Panitia Tender?
          </p>

          <div className='border rounded overflow-hidden mb-3'>
            {[
              {
                label: 'Paket Pengadaan:',
                value: <b>{record?.title || '-'}</b>,
              },
              {
                label: 'Kode Procurement:',
                value: (
                  <span className='badge bg-info-subtle text-info font-monospace'>
                    {record?.procId || '-'}
                  </span>
                ),
              },
              {
                label: 'Judul Nota Dinas:',
                value: <b>{form.notaDinasTitle || '-'}</b>,
              },
              {
                label: 'Nomor Nota Dinas:',
                value: (
                  <span className='font-monospace fw-semibold'>
                    {form.notaDinasNo || '-'}
                  </span>
                ),
              },
              {
                label: 'Tanggal Nota Dinas:',
                value: <b>{form.notaDinasDate || '-'}</b>,
              },
              {
                label: 'Jadwal Rapat (WIB):',
                value: (
                  <span className='font-monospace fw-semibold text-primary'>
                    {form.meetingDate
                      ? `${form.meetingDate}T${form.startTime || '--:--'} s/d ${
                          form.isUntilFinished
                            ? 'selesai'
                            : `${form.meetingEndDate || form.meetingDate}T${form.endTime || '--:--'}`
                        }`
                      : '-'}
                  </span>
                ),
              },
              {
                label: 'Lokasi Kegiatan:',
                value: <b>{form.location || '-'}</b>,
              },
              {
                label: 'File Terlampir:',
                value: (
                  <span
                    className='font-monospace fs-12 d-inline-block text-truncate'
                    style={{ maxWidth: 320 }}
                  >
                    {form.file?.name || p.notaDinasDocument || '-'}
                  </span>
                ),
              },
            ].map((row, idx) => (
              <div
                key={row.label}
                className={`d-flex justify-content-between align-items-start gap-3 px-3 py-2 ${
                  idx % 2 === 0 ? 'bg-light' : ''
                } ${idx > 0 ? 'border-top' : ''}`}
              >
                <span className='text-muted fs-13 flex-shrink-0'>
                  {row.label}
                </span>
                <span className='text-end'>{row.value}</span>
              </div>
            ))}
          </div>

          <div className='border border-success-subtle bg-success-subtle bg-opacity-25 rounded p-3'>
            <p className='fw-semibold text-success mb-2'>
              <i className='ri-checkbox-circle-line align-bottom me-1'></i>
              Dampak &amp; Alur Proses Lanjutan:
            </p>
            <ul className='mb-0 ps-3 fs-13'>
              <li className='mb-1'>
                Undangan ini akan{' '}
                <b>dilanjutkan ke proses Invitation to Tender Evaluation</b> dan
                didistribusikan ke <b>Role Panitia Tender Observer</b>.
              </li>
              <li>
                Paket tender ini akan otomatis muncul pada menu{' '}
                <b>
                  Modul for Panitia Tender Observer &rarr; a. Invitation for
                  Tender Evaluation
                </b>{' '}
                dengan status{' '}
                <span className='badge bg-success-subtle text-success'>
                  Open
                </span>{' '}
                agar Observer dapat melakukan konfirmasi kehadiran atau delegasi
                rapat evaluasi.
              </li>
            </ul>
          </div>

          <div className='d-flex justify-content-center gap-2 mt-4'>
            <Button
              color='light'
              className='border px-4'
              onClick={() => setConfirmOpen(false)}
            >
              Batal
            </Button>
            <Button color='primary' className='px-4' onClick={confirmSend}>
              <i className='ri-send-plane-line align-bottom me-1'></i>
              Ya, Kirim Undangan Panitia Tender
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default SendPanitiaTab;
